import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { listAuditEvents } from "@/lib/admin.functions";
import { FileClock } from "lucide-react";

export const Route = createFileRoute("/_admin/admin/audit-log")({
  component: AuditLogPage,
});

type AuditRow = {
  id: string;
  occurred_at: string;
  actor_id: string | null;
  actor_email: string | null;
  actor_role: string | null;
  action: string;
  table_name: string | null;
  record_id: string | null;
  changed_columns: string[] | null;
  old_data: unknown;
  new_data: unknown;
  context: string | null;
  ip: string | null;
  user_agent: string | null;
};

const RANGE_OPTIONS = [
  { hours: 24, label: "24 Std." },
  { hours: 24 * 7, label: "7 Tage" },
  { hours: 24 * 30, label: "30 Tage" },
  { hours: 24 * 90, label: "90 Tage" },
] as const;

const TABLE_OPTIONS = [
  "",
  "user_roles",
  "profiles",
  "cases",
  "blog_posts",
  "projects",
  "testimonials",
  "authors",
  "client_logos",
  "email_unsubscribe_tokens",
  "suppressed_emails",
  "audit_log",
];

const ACTION_OPTIONS = [
  "",
  "INSERT",
  "UPDATE",
  "DELETE",
  "READ_BULK",
  "EMAIL_SEND",
  "ROLE_GRANT",
  "EXPORT",
  "PUBLISH",
  "UNPUBLISH",
];

function actionBadge(action: string): string {
  switch (action) {
    case "DELETE":
      return "bg-destructive/15 text-destructive";
    case "INSERT":
      return "bg-brand-wahlen/15 text-brand-wahlen";
    case "UPDATE":
      return "bg-primary/10 text-primary";
    case "READ_BULK":
    case "EXPORT":
      return "bg-secondary text-muted-foreground";
    default:
      return "bg-primary/10 text-primary";
  }
}

function AuditLogPage() {
  const fetchEvents = useServerFn(listAuditEvents);
  const [sinceHours, setSinceHours] = useState<number>(24 * 7);
  const [table, setTable] = useState<string>("");
  const [action, setAction] = useState<string>("");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["audit-log", sinceHours, table, action],
    queryFn: () =>
      fetchEvents({
        data: {
          sinceHours,
          limit: 200,
          table: table || null,
          action: action || null,
        },
      }),
  });

  const rows: AuditRow[] = useMemo(() => (data?.events ?? []) as AuditRow[], [data]);
  const open = rows.find((r) => r.id === openId) ?? null;

  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-primary">Audit-Log</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Änderungen an sensiblen Tabellen und protokollierte Ereignisse aus dem Backend.
            Nur für Administratoren sichtbar. Aufbewahrung: 365 Tage.
          </p>
        </div>
        <FileClock className="h-8 w-8 shrink-0 text-primary" aria-hidden />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {RANGE_OPTIONS.map((opt) => (
          <button
            key={opt.hours}
            onClick={() => setSinceHours(opt.hours)}
            className={
              "rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors " +
              (sinceHours === opt.hours
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:bg-secondary")
            }
          >
            {opt.label}
          </button>
        ))}
        <select
          value={table}
          onChange={(e) => setTable(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground"
        >
          {TABLE_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {t === "" ? "Alle Tabellen" : t}
            </option>
          ))}
        </select>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold text-foreground"
        >
          {ACTION_OPTIONS.map((a) => (
            <option key={a} value={a}>
              {a === "" ? "Alle Aktionen" : a}
            </option>
          ))}
        </select>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="ml-auto rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary disabled:opacity-60"
        >
          {isFetching ? "Aktualisiere…" : "Aktualisieren"}
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="bg-secondary/60 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5">Zeit</th>
              <th className="px-3 py-2.5">Aktion</th>
              <th className="px-3 py-2.5">Tabelle</th>
              <th className="px-3 py-2.5">Record</th>
              <th className="px-3 py-2.5">Akteur</th>
              <th className="px-3 py-2.5">Rolle</th>
              <th className="px-3 py-2.5">Kontext</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                  Lade Ereignisse…
                </td>
              </tr>
            )}
            {error && !isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-destructive">
                  Konnte Audit-Log nicht laden.
                </td>
              </tr>
            )}
            {!isLoading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                  Keine Ereignisse im gewählten Zeitraum.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="align-top">
                <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted-foreground">
                  {new Date(row.occurred_at).toLocaleString("de-DE")}
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={
                      "inline-flex rounded px-2 py-0.5 text-xs font-semibold " +
                      actionBadge(row.action)
                    }
                  >
                    {row.action}
                  </span>
                </td>
                <td className="px-3 py-2.5 font-mono text-xs">{row.table_name ?? "—"}</td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-muted-foreground">
                  {row.record_id ? row.record_id.slice(0, 8) + "…" : "—"}
                </td>
                <td className="px-3 py-2.5 text-xs">
                  {row.actor_email ?? (
                    <span className="text-muted-foreground">System / Trigger</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-xs">{row.actor_role ?? "—"}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">
                  {row.context ?? "—"}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button
                    onClick={() => setOpenId(row.id)}
                    className="rounded border border-border px-2 py-1 text-xs font-semibold hover:bg-secondary"
                  >
                    Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Maximal 200 Einträge je Abfrage. Bulk-Zugriffe auf das Audit-Log werden ihrerseits
        protokolliert.
      </p>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpenId(null)}
        >
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-lg border border-border bg-background p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-extrabold text-primary">
                  {open.action} · {open.table_name ?? "—"}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(open.occurred_at).toLocaleString("de-DE")} · Akteur:{" "}
                  {open.actor_email ?? "System"} ({open.actor_role ?? "—"})
                </p>
                {open.record_id && (
                  <p className="mt-1 font-mono text-xs text-muted-foreground">
                    id: {open.record_id}
                  </p>
                )}
              </div>
              <button
                onClick={() => setOpenId(null)}
                className="rounded border border-border px-2 py-1 text-xs font-semibold hover:bg-secondary"
              >
                Schließen
              </button>
            </div>

            {open.changed_columns && open.changed_columns.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Geänderte Felder
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {open.changed_columns.map((c) => (
                    <span
                      key={c}
                      className="rounded bg-secondary px-2 py-0.5 font-mono text-[11px]"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Vorher
                </p>
                <pre className="mt-1 max-h-96 overflow-auto rounded border border-border bg-secondary/40 p-2 text-[11px]">
                  {open.old_data ? JSON.stringify(open.old_data, null, 2) : "—"}
                </pre>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Nachher
                </p>
                <pre className="mt-1 max-h-96 overflow-auto rounded border border-border bg-secondary/40 p-2 text-[11px]">
                  {open.new_data ? JSON.stringify(open.new_data, null, 2) : "—"}
                </pre>
              </div>
            </div>

            {(open.ip || open.user_agent) && (
              <div className="mt-4 text-xs text-muted-foreground">
                {open.ip && <div>IP: {open.ip}</div>}
                {open.user_agent && <div>UA: {open.user_agent}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
